import {DosageLimitsMatrix} from '@/modules/drug-catalog/components/dosage-limits-matrix'
import {
  DrugItemDrawerAboutSection,
  DrugItemDrawerClassificationSection,
} from '@/modules/drug-catalog/components/drug-item-drawer/drug-item-drawer-sections'
import {DataList} from '@cypro/ui/components/ui/data-list'
import {DataSection} from '@cypro/ui/components/ui/data-section'
import {useActiveSubstanceDrugContext} from '@/shared/contexts/active-substance-drug'
import {Tabs} from '@chakra-ui/react'
import {useTranslate} from '@tolgee/react'
import {useMemo} from 'react'

export function AboutTab() {
  const {t} = useTranslate('common')
  const {activeSubstanceDrug} = useActiveSubstanceDrugContext()

  const doseLimits = activeSubstanceDrug?.activeSubstanceDetails?.doseLimits

  const {dosing, properties, safetyLimits} = useMemo(() => {
    return {
      dosing: [
        {
          label: t('calculation_mode'),
          value: activeSubstanceDrug.activeSubstanceDetails?.doseCalcMode
            ? t(activeSubstanceDrug.activeSubstanceDetails?.doseCalcMode)
            : undefined,
        },
        {label: t('unit'), value: activeSubstanceDrug.activeSubstanceDetails?.doseUnit?.ucumCode},
        {
          label: t('rounding_mode'),
          value: activeSubstanceDrug.activeSubstanceDetails?.roundingMode
            ? t(activeSubstanceDrug.activeSubstanceDetails?.roundingMode)
            : null,
        },
      ],

      safetyLimits: [
        {label: t('dose_limit_unit'), value: activeSubstanceDrug.activeSubstanceDetails?.doseUnit?.ucumCode},

        {label: t('min_interval'), value: activeSubstanceDrug.activeSubstanceDetails?.minIntervalValue},
        {
          label: t('min_interval_unit'),
          value: activeSubstanceDrug.activeSubstanceDetails?.minIntervalDurationUnit
            ? t(activeSubstanceDrug.activeSubstanceDetails?.minIntervalDurationUnit)
            : null,
        },
        {
          label: t('overdose_level'),
          value: activeSubstanceDrug.activeSubstanceDetails?.overdosePreventionLevel
            ? t(activeSubstanceDrug.activeSubstanceDetails?.overdosePreventionLevel)
            : null,
        },
      ],

      properties: [
        {
          label: t('requires_additional_approval'),
          value: activeSubstanceDrug.activeSubstanceDetails?.additionalApprovalRequired ? t('yes') : t('no'),
        },
        {
          label: t('cytotoxic'),
          value: activeSubstanceDrug.activeSubstanceDetails?.cytotoxic ? t('yes') : t('no'),
        },
        {
          label: t('route_warning'),
          value: activeSubstanceDrug.activeSubstanceDetails?.routeWarning ? t('yes') : t('no'),
        },
        {
          label: t('hazard_class'),
          value: activeSubstanceDrug.activeSubstanceDetails?.hazardClasses
            ?.map(j => j.hazardClass?.name)
            .filter(Boolean)
            .join(', '),
        },
        {
          label: t('has_salt_content'),
          value: activeSubstanceDrug.activeSubstanceDetails?.hasSaltContent ? t('yes') : t('no'),
        },
        {label: t('salt_content_notes'), value: activeSubstanceDrug.activeSubstanceDetails?.saltContentNotes},
        {label: t('molar_mass_salt_base'), value: activeSubstanceDrug.activeSubstanceDetails?.molarMassSaltBase},
        {label: t('molar_mass_free_base'), value: activeSubstanceDrug.activeSubstanceDetails?.molarMassFreeBase},
        {label: t('salt_factor'), value: activeSubstanceDrug.activeSubstanceDetails?.saltFactor},
        {
          label: t('preparation_type'),
          value: activeSubstanceDrug.activeSubstanceDetails?.preparationType
            ? t(activeSubstanceDrug.activeSubstanceDetails?.preparationType)
            : null,
        },
      ],
    }
  }, [t, activeSubstanceDrug])

  return (
    <Tabs.Content value="about" spaceY="8" display="flex" flexDirection="column">
      <DrugItemDrawerAboutSection drugItem={activeSubstanceDrug} />

      <DrugItemDrawerClassificationSection drugItem={activeSubstanceDrug} />

      <DataSection label={t('dosing')}>
        <DataList items={dosing} />
      </DataSection>

      <DataSection label={t('safety_limits')}>
        <DataList items={safetyLimits} />

        <DosageLimitsMatrix
          bordered
          color="fg.muted"
          fontWeight="normal"
          unit={activeSubstanceDrug.activeSubstanceDetails?.doseUnit?.ucumCode}
          renderCellContent={({row, col}) => doseLimits?.[row.key]?.[col.key]}
        />
      </DataSection>

      <DataSection label={t('properties')}>
        <DataList items={properties} />
      </DataSection>
    </Tabs.Content>
  )
}
